import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  HelpCircle,
  Lightbulb,
  Mic,
  Paperclip,
  Target,
  Users,
  X,
} from 'lucide-react';

import { cn } from '../../../lib/cn';
import {
  parseSessionDetail,
  type ParsedSessionDetail,
  type SessionFlowStep,
} from '../../lib/parseSessionDetail';

interface SessionDetailRendererProps {
  rawContent: string;
  className?: string;
  /** OA externo (p. ej. desde foco del hito) si no viene en el resumen */
  oaCode?: string;
  /** Sección de materiales vinculados — se renderiza al final del timeline */
  linkedMaterialsFooter?: React.ReactNode;
}

const META_LABEL = 'text-[10px] font-medium uppercase tracking-wide text-[var(--sala-ink-muted)]';

function SessionObjectiveHeader({
  oaCode,
  objective,
}: {
  oaCode?: string;
  objective: string;
}) {
  return (
    <header className="mb-8">
      <span className="mb-2 block text-xs uppercase tracking-widest text-gray-400">
        Objetivo de la sesión
      </span>
      {oaCode ? (
        <span className="mb-3 inline-block font-mono text-[11px] tracking-tight text-[var(--sala-ink-muted)]">
          {oaCode}
        </span>
      ) : null}
      <p className="text-xl font-medium tracking-tight text-gray-900 md:text-2xl">{objective}</p>
    </header>
  );
}

const ROLE_OVERLINE =
  'mb-1.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-[var(--sala-ink-muted)]';

function TeacherScriptBlock({
  text,
  icon: Icon,
}: {
  text: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="border-l border-gray-200/80 pl-3">
      <p className={ROLE_OVERLINE}>
        <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
        Docente
      </p>
      <p className="text-sm leading-relaxed text-[var(--sala-ink)]">{text}</p>
    </div>
  );
}

function StudentScriptBlock({
  text,
  icon: Icon,
}: {
  text: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="border-l border-gray-100 pl-3">
      <p className={ROLE_OVERLINE}>
        <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
        Estudiantes
      </p>
      <p className="text-sm leading-relaxed text-[var(--sala-ink-muted)]">{text}</p>
    </div>
  );
}

function DrawerSection({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-[var(--sala-ink-muted)]" aria-hidden />
        <h4 className="text-xs font-semibold text-[var(--sala-ink)]">{title}</h4>
      </div>
      <p className="text-sm leading-relaxed text-[var(--sala-ink-muted)]">{children}</p>
    </section>
  );
}

function StepInsightDrawer({
  step,
  evidence,
  tips,
  open,
  onClose,
}: {
  step: SessionFlowStep | null;
  evidence?: string;
  tips?: string;
  open: boolean;
  onClose: () => void;
}) {
  if (!open || !step || typeof document === 'undefined') return null;

  const materials = step.roles.materials?.trim();
  const hasTips = Boolean(tips?.trim());
  const hasMaterials = Boolean(materials);
  const hasEvidence = Boolean(evidence?.trim());

  return createPortal(
    <>
      <button
        type="button"
        className="fixed inset-0 z-[80] bg-black/20 backdrop-blur-[1px]"
        aria-label="Cerrar panel"
        onClick={onClose}
      />
      <aside
        className="fixed inset-y-0 right-0 z-[90] flex w-full max-w-sm flex-col border-l border-[var(--sala-border-strong)] bg-[var(--sala-surface-raised,var(--sala-surface))] shadow-2xl animate-in slide-in-from-right duration-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="step-insight-title"
      >
        <header className="flex items-start justify-between gap-3 border-b border-[var(--sala-border-strong)] px-5 py-4">
          <div className="min-w-0">
            <p className={META_LABEL}>Mediación del paso</p>
            <h3 id="step-insight-title" className="mt-1 text-sm font-semibold text-[var(--sala-ink)]">
              {step.title}
            </h3>
            {step.durationLabel ? (
              <p className="mt-1 font-mono text-[11px] text-[var(--sala-ink-muted)]">
                {step.durationLabel}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="rounded-md p-1.5 text-[var(--sala-ink-muted)] transition hover:text-[var(--sala-ink)]"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
          {hasTips ? (
            <DrawerSection title="Tips de mediación" icon={Lightbulb}>
              {tips}
            </DrawerSection>
          ) : null}

          {hasMaterials ? (
            <DrawerSection title="Materiales" icon={Paperclip}>
              {materials}
            </DrawerSection>
          ) : null}

          {hasEvidence ? (
            <DrawerSection title="Evidencia de avance" icon={Target}>
              {evidence}
            </DrawerSection>
          ) : null}

          {!hasTips && !hasMaterials && !hasEvidence ? (
            <p className="text-sm text-[var(--sala-ink-muted)]">
              No hay sugerencias adicionales para este paso.
            </p>
          ) : null}
        </div>
      </aside>
    </>,
    document.body,
  );
}

function CompactFlowTimeline({
  steps,
  evidence,
  tips,
}: {
  steps: SessionFlowStep[];
  evidence?: string;
  tips?: string;
}) {
  const [activeStepIndex, setActiveStepIndex] = useState<number | null>(null);
  const activeStep = activeStepIndex != null ? steps[activeStepIndex] ?? null : null;

  const openDrawer = (index: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setActiveStepIndex(index);
  };

  return (
    <>
      <section className="w-full">
        <p className={cn(META_LABEL, 'mb-6')}>Coreografía de la sesión</p>
        <ol className="relative border-l border-gray-200/70 pl-5">
          {steps.map((step, index) => {
            const hasTeacher = Boolean(step.roles.teacher?.trim());
            const hasStudents = Boolean(step.roles.students?.trim());
            const hasDrawerContent = Boolean(
              tips?.trim() || step.roles.materials?.trim() || evidence?.trim(),
            );
            const isLast = index === steps.length - 1;

            return (
              <li
                key={`${step.title}-${index}`}
                className={cn(
                  'relative py-8',
                  !isLast && 'border-b border-gray-100/50',
                )}
              >
                <span
                  className="absolute -left-[calc(1.25rem+1px)] top-9 h-2.5 w-2.5 rounded-full border border-gray-300 bg-[var(--sala-surface-raised,var(--sala-surface))]"
                  aria-hidden
                />

                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <h4 className="text-base font-semibold leading-snug text-[var(--sala-ink)]">
                        {step.title}
                      </h4>
                      {step.durationLabel ? (
                        <span className="shrink-0 font-mono text-xs text-[var(--sala-ink-muted)]">
                          {step.durationLabel}
                        </span>
                      ) : null}
                    </div>

                    {(hasTeacher || hasStudents) && (
                      <div className="mt-5 space-y-4">
                        {hasTeacher ? (
                          <TeacherScriptBlock text={step.roles.teacher!} icon={Mic} />
                        ) : null}
                        {hasStudents ? (
                          <StudentScriptBlock text={step.roles.students!} icon={Users} />
                        ) : null}
                      </div>
                    )}
                  </div>

                  {hasDrawerContent ? (
                    <button
                      type="button"
                      className="shrink-0 rounded-md p-1.5 text-[var(--sala-ink-muted)] transition-colors hover:text-[var(--sala-ink)]"
                      aria-label={`Ver tips, materiales y evidencia del paso ${step.title}`}
                      title="Tips, materiales y evidencia"
                      onClick={(event) => openDrawer(index, event)}
                    >
                      <HelpCircle className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      </section>

      <StepInsightDrawer
        step={activeStep}
        evidence={evidence}
        tips={tips}
        open={activeStepIndex != null}
        onClose={() => setActiveStepIndex(null)}
      />
    </>
  );
}

function RawFallback({ content }: { content: string }) {
  return (
    <pre className="whitespace-pre-wrap text-xs leading-relaxed text-[var(--sala-ink-muted)]">
      {content}
    </pre>
  );
}

function hasRenderableContent(parsed: ParsedSessionDetail): boolean {
  return Boolean(
    parsed.learningObjective ||
      parsed.summary ||
      parsed.flow.length > 0 ||
      parsed.evidence ||
      parsed.tips,
  );
}

export function SessionDetailRenderer({
  rawContent,
  className,
  oaCode: oaCodeProp,
  linkedMaterialsFooter,
}: SessionDetailRendererProps) {
  const parsed = useMemo(() => parseSessionDetail(rawContent), [rawContent]);
  const oaCode = oaCodeProp ?? parsed.oaCode;
  const objective = parsed.learningObjective ?? parsed.summary;

  if (!rawContent.trim()) return null;

  if (!hasRenderableContent(parsed)) {
    return (
      <div className={cn('w-full', className)}>
        <RawFallback content={rawContent.trim()} />
        {linkedMaterialsFooter}
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)}>
      {objective ? (
        <SessionObjectiveHeader oaCode={oaCode} objective={objective} />
      ) : null}
      {parsed.flow.length > 0 ? (
        <CompactFlowTimeline steps={parsed.flow} evidence={parsed.evidence} tips={parsed.tips} />
      ) : null}
      {linkedMaterialsFooter}
    </div>
  );
}
